/**
 * The words of the employee dialog's legal-minimum box (Q25 a, agreed by Rob 22/10/2026).
 *
 * The old box packed two different "19"s into one sentence — the legal minimum cut for the
 * start date, and the allowance the admin had typed — and Rob read it three times before
 * asking what it meant. Now it is three short lines: why the year is short (only when it
 * is), the minimum, and what this person actually gets, built up from its parts.
 *
 * Pure, so `statutoryBox.checks.mjs` can pin the wording. `info` is GET
 * /entitlement/advisory/{id}?annual_allowance= (routers/entitlement.py) plus `asked_allowance`
 * — the figure that answer was FOR, kept by the dialog so a slow reply can't be worded
 * against a different number from the one it priced.
 */
import { formatDateGB } from './dates.js';

/** 19 -> "19", 18.5 -> "18.5", 28.0 -> "28". */
export const num = (n) => {
  const v = Math.round(Number(n) * 10) / 10;
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

/** 1 -> "1 day", 0.5 -> "0.5 days", 19 -> "19 days". */
export const daysWord = (n) => `${num(n)} ${num(n) === '1' ? 'day' : 'days'}`;

/** "5 days a week × 5.6 weeks", with ", capped at 28" when the law caps it. */
export const sumText = (info) => {
  const dpw = info?.days_a_week;
  const weeks = info?.weeks;
  if (!dpw || !weeks) return null;
  const full = Number(info.statutory_days_full_year ?? info.statutory_days);
  const uncapped = Number(dpw) * Number(weeks);
  const cap = Number.isFinite(full) && uncapped > full + 0.05 ? `, capped at ${num(full)}` : '';
  return `${num(dpw)} ${num(dpw) === '1' ? 'day' : 'days'} a week × ${num(weeks)} weeks${cap}`;
};

/**
 * Everything the box says, or null when there is nothing to say.
 *   { below: bool, title, intro|null, minimum: {days, detail|null},
 *     gets: {days, detail|null}|null, short|null }
 */
export const statutoryBox = ({ info, firstName, startDate }) => {
  if (!info || info.meets === null || info.meets === undefined) return null;
  const who = (firstName || '').trim() || 'This person';
  const prorated = Boolean(info.prorated);
  const sum = sumText(info);

  const intro = prorated && startDate
    ? `${who} started on ${formatDateGB(startDate)}, so only part of this year counts.`
    : null;

  // The minimum: this year's figure, and where it comes from.
  let minDetail = null;
  if (prorated) {
    minDetail = `${num(info.statutory_days_full_year)} for a full year${sum ? ` — ${sum}` : ''}`;
  } else if (sum) {
    minDetail = sum;
  }

  // What they get: the allowance part, then the bank holidays on top.
  let gets = null;
  if (info.total_days !== null && info.total_days !== undefined) {
    const parts = [];
    const fromAllowance = info.allowance_days_in_total;
    const typed = info.asked_allowance;
    const bh = Number(info.bank_holiday_days_in_total || 0);
    if (fromAllowance !== null && fromAllowance !== undefined) {
      const a = Number(fromAllowance);
      const t = Number(typed);
      let why = '';
      if (Number.isFinite(t) && Math.abs(a - t) >= 0.05) {
        if (a < t && prorated) why = ` (cut for the start date)`;
        else if (a > t) why = ` (with extra days for years worked)`;
        else why = ` (adjusted for their dates)`;
        parts.push(`${num(a)} from the ${num(t)}-day allowance${why}`);
      } else {
        parts.push(`${num(a)} of allowance`);
      }
    }
    if (bh > 0) parts.push(`${num(bh)} bank ${num(bh) === '1' ? 'holiday' : 'holidays'}`);
    gets = {
      days: daysWord(info.total_days),
      // A lone "13 of allowance" says nothing the total didn't.
      detail: parts.length > 1 || (parts.length === 1 && bh === 0 && /from the/.test(parts[0]))
        ? parts.join(' + ') : null,
    };
  }

  const below = info.meets === false;
  return {
    below,
    who,
    title: below ? 'Below the legal minimum (advisory)' : 'Meets the legal minimum',
    intro,
    minimum: { label: prorated ? 'Legal minimum this year' : 'Legal minimum', days: daysWord(info.statutory_days), detail: minDetail },
    gets: gets && { label: prorated ? `${who} gets this year` : `${who} gets`, ...gets },
    short: below && info.shortfall_days ? `${daysWord(info.shortfall_days)} short.` : null,
  };
};
